import React, { useState, useEffect, useCallback } from 'react';
import { 
  Scale, Upload, Search, Filter, Plus, FileText, Calendar, Building2, 
  Tag, Trash2, Eye, Download, Star, StarOff, Bot, MessageSquare,
  ChevronDown, ChevronRight, FolderOpen, RefreshCw, FileUp, X,
  Sparkles, Brain, PenTool, List, BarChart3
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from '../hooks/use-toast';
import { sentenciasAPI } from '../api';

const Sentencias = () => {
  // Estados principales
  const [sentencias, setSentencias] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [estadisticas, setEstadisticas] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('listado');
  
  // Filtros
  const [filtros, setFiltros] = useState({
    busqueda: '',
    categoria_id: null,
    materia: '',
    destacadas: false
  });
  
  // Modales
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showDetalleModal, setShowDetalleModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showIAModal, setShowIAModal] = useState(false);
  const [sentenciaSeleccionada, setSentenciaSeleccionada] = useState(null);
  const [sentenciasSeleccionadas, setSentenciasSeleccionadas] = useState([]);
  
  // Estado de subida
  const [uploadData, setUploadData] = useState({
    archivo: null,
    juzgado: '',
    numero_sentencia: '',
    fecha: '',
    materia: '',
    categoria_id: '',
    subtema: '',
    notas: ''
  });
  const [uploading, setUploading] = useState(false);
  
  // Estado de edición
  const [editData, setEditData] = useState({
    juzgado: '',
    numero_sentencia: '',
    fecha: '',
    materia: '',
    categoria_id: '',
    subtema: '',
    notas: ''
  });
  const [saving, setSaving] = useState(false);
  
  // Estado de IA
  const [iaQuery, setIaQuery] = useState('');
  const [iaResponse, setIaResponse] = useState(null);
  const [iaLoading, setIaLoading] = useState(false);
  const [iaTipoAnalisis, setIaTipoAnalisis] = useState('consultar');

  // Cargar datos iniciales
  useEffect(() => {
    const cargarDatosIniciales = async () => {
      setLoading(true);
      try {
        // Inicializar categorías si no existen
        await sentenciasAPI.inicializarCategorias().catch(() => {});
        
        // Cargar categorías
        const catRes = await sentenciasAPI.getCategorias();
        setCategorias(catRes.data || []);
        
        // Cargar sentencias
        const sentRes = await sentenciasAPI.getAll({});
        setSentencias(sentRes.data || []);
        
        // Cargar estadísticas
        const statsRes = await sentenciasAPI.getEstadisticas();
        setEstadisticas(statsRes.data);
        
      } catch (error) {
        console.error('Error cargando datos:', error);
      } finally {
        setLoading(false);
      }
    };
    
    cargarDatosIniciales();
  }, []);

  const cargarSentencias = useCallback(async () => {
    try {
      const params = {};
      if (filtros.busqueda) params.busqueda = filtros.busqueda;
      if (filtros.categoria_id) params.categoria_id = filtros.categoria_id;
      if (filtros.materia) params.materia = filtros.materia;
      if (filtros.destacadas) params.destacadas = true;
      
      const res = await sentenciasAPI.getAll(params);
      setSentencias(res.data);
    } catch (error) {
      console.error('Error cargando sentencias:', error);
    }
  }, [filtros]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      cargarSentencias();
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [filtros, cargarSentencias]);

  // Subir sentencia
  const handleUpload = async (e) => {
    e.preventDefault();
    if (!uploadData.archivo) {
      toast({ title: "Error", description: "Selecciona un archivo PDF", variant: "destructive" });
      return;
    }
    
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('archivo', uploadData.archivo);
      formData.append('juzgado', uploadData.juzgado);
      formData.append('numero_sentencia', uploadData.numero_sentencia);
      formData.append('fecha', uploadData.fecha);
      formData.append('materia', uploadData.materia);
      if (uploadData.categoria_id) formData.append('categoria_id', uploadData.categoria_id);
      if (uploadData.subtema) formData.append('subtema', uploadData.subtema);
      if (uploadData.notas) formData.append('notas', uploadData.notas);
      
      await sentenciasAPI.upload(formData);
      
      toast({ title: "Éxito", description: "Sentencia subida correctamente" });
      setShowUploadModal(false);
      setUploadData({
        archivo: null, juzgado: '', numero_sentencia: '', fecha: '',
        materia: '', categoria_id: '', subtema: '', notas: ''
      });
      cargarSentencias();
      
    } catch (error) {
      toast({ title: "Error", description: error.response?.data?.detail || "Error al subir", variant: "destructive" });
    } finally {
      setUploading(false);
    }
  };

  // Ver detalle
  const verDetalle = async (id) => {
    try {
      const res = await sentenciasAPI.getById(id);
      setSentenciaSeleccionada(res.data);
      setShowDetalleModal(true);
    } catch (error) {
      toast({ title: "Error", description: "No se pudo cargar la sentencia", variant: "destructive" });
    }
  };

  // Generar resumen IA
  const generarResumen = async (id) => {
    setIaLoading(true);
    try {
      const res = await sentenciasAPI.generarResumen(id);
      toast({ title: "Resumen generado", description: "El resumen se ha guardado en la sentencia" });
      if (sentenciaSeleccionada?.id === id) {
        setSentenciaSeleccionada({ ...sentenciaSeleccionada, resumen_ia: res.data.resumen });
      }
      cargarSentencias();
    } catch (error) {
      toast({ title: "Error", description: error.response?.data?.detail || "Error generando resumen", variant: "destructive" });
    } finally {
      setIaLoading(false);
    }
  };

  // Toggle destacada
  const toggleDestacada = async (sentencia) => {
    try {
      await sentenciasAPI.update(sentencia.id, {
        destacada: !sentencia.destacada
      });
      cargarSentencias();
    } catch (error) {
      toast({ title: "Error", description: "No se pudo actualizar", variant: "destructive" });
    }
  };

  // Abrir modal de edición
  const abrirEdicion = (sentencia) => {
    setEditData({
      juzgado: sentencia.juzgado || '',
      numero_sentencia: sentencia.numero_sentencia || '',
      fecha: sentencia.fecha ? sentencia.fecha.split('T')[0] : '',
      materia: sentencia.materia || '',
      categoria_id: sentencia.categoria_id || '',
      subtema: sentencia.subtema || '',
      notas: sentencia.notas || ''
    });
    setShowDetalleModal(false);
    setShowEditModal(true);
  };

  // Guardar edición
  const guardarEdicion = async (e) => {
    e.preventDefault();
    if (!sentenciaSeleccionada) return;
    
    setSaving(true);
    try {
      await sentenciasAPI.update(sentenciaSeleccionada.id, editData);
      toast({ title: "Guardado", description: "Sentencia actualizada correctamente" });
      setShowEditModal(false);
      cargarSentencias();
    } catch (error) {
      toast({ title: "Error", description: "No se pudo guardar", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  // Eliminar sentencia
  const eliminarSentencia = async (id) => {
    if (!window.confirm('¿Eliminar esta sentencia? Esta acción no se puede deshacer.')) return;
    
    try {
      await sentenciasAPI.delete(id);
      toast({ title: "Eliminada", description: "Sentencia eliminada correctamente" });
      setShowDetalleModal(false);
      setShowEditModal(false);
      cargarSentencias();
    } catch (error) {
      toast({ title: "Error", description: "No se pudo eliminar", variant: "destructive" });
    }
  };

  // Consultar IA
  const consultarIA = async () => {
    if (!iaQuery.trim()) return;
    
    setIaLoading(true);
    setIaResponse(null);
    try {
      if (iaTipoAnalisis === 'consultar') {
        const res = await sentenciasAPI.consultar({
          query: iaQuery,
          categoria_id: filtros.categoria_id || null,
          incluir_texto: true
        });
        setIaResponse(res.data);
      } else if (sentenciasSeleccionadas.length > 0) {
        const res = await sentenciasAPI.analizarMultiples({
          sentencia_ids: sentenciasSeleccionadas,
          tipo_analisis: iaTipoAnalisis,
          consulta: iaQuery
        });
        setIaResponse(res.data);
      } else {
        toast({ title: "Selecciona sentencias", description: "Para este análisis, selecciona una o más sentencias", variant: "destructive" });
      }
    } catch (error) {
      toast({ title: "Error", description: error.response?.data?.detail || "Error consultando IA", variant: "destructive" });
    } finally {
      setIaLoading(false);
    }
  };

  // Toggle selección para análisis múltiple
  const toggleSeleccion = (id) => {
    setSentenciasSeleccionadas(prev => 
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  // Formatear fecha
  const formatFecha = (fecha) => {
    if (!fecha) return 'N/A';
    return new Date(fecha).toLocaleDateString('es-ES', { 
      day: '2-digit', month: '2-digit', year: 'numeric' 
    });
  };

  // Formatear tamaño
  const formatSize = (bytes) => {
    if (!bytes) return '0 KB';
    const kb = bytes / 1024;
    return kb > 1024 ? `${(kb / 1024).toFixed(1)} MB` : `${kb.toFixed(0)} KB`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Scale className="w-7 h-7 text-blue-600" />
            Sentencias
          </h1>
          <p className="text-gray-600 mt-1">
            Fondo jurisprudencial propio • {estadisticas?.total_sentencias || 0} sentencias
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowIAModal(true)}>
            <Bot className="w-4 h-4 mr-2" />
            Consultar IA
          </Button>
          <Button onClick={() => setShowUploadModal(true)} className="bg-blue-600 hover:bg-blue-700">
            <Upload className="w-4 h-4 mr-2" />
            Subir Sentencia
          </Button>
        </div>
      </div>

      {/* Estadísticas rápidas */}
      {estadisticas && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <FileText className="w-8 h-8 text-blue-600" />
                <div>
                  <p className="text-2xl font-bold text-blue-700">{estadisticas.total_sentencias}</p>
                  <p className="text-sm text-blue-600">Total Sentencias</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-yellow-50 to-yellow-100 border-yellow-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <Star className="w-8 h-8 text-yellow-600" />
                <div>
                  <p className="text-2xl font-bold text-yellow-700">{estadisticas.destacadas}</p>
                  <p className="text-sm text-yellow-600">Destacadas</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <FolderOpen className="w-8 h-8 text-purple-600" />
                <div>
                  <p className="text-2xl font-bold text-purple-700">{categorias.length}</p>
                  <p className="text-sm text-purple-600">Categorías</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <BarChart3 className="w-8 h-8 text-green-600" />
                <div>
                  <p className="text-2xl font-bold text-green-700">{estadisticas.por_materia?.length || 0}</p>
                  <p className="text-sm text-green-600">Materias</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filtros y búsqueda */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Buscar por juzgado, número, texto..."
                value={filtros.busqueda}
                onChange={(e) => setFiltros({...filtros, busqueda: e.target.value})}
                className="pl-10"
              />
            </div>
            <Select 
              value={filtros.categoria_id?.toString() || 'all'} 
              onValueChange={(v) => setFiltros({...filtros, categoria_id: v === 'all' ? null : parseInt(v)})}
            >
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="Categoría" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas las categorías</SelectItem>
                {categorias.map(cat => (
                  <SelectItem key={cat.id} value={cat.id.toString()}>
                    {cat.nombre} ({cat.total_sentencias})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button 
              variant={filtros.destacadas ? "default" : "outline"}
              onClick={() => setFiltros({...filtros, destacadas: !filtros.destacadas})}
            >
              <Star className={`w-4 h-4 mr-1 ${filtros.destacadas ? 'fill-current' : ''}`} />
              Destacadas
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Selección múltiple */}
      {sentenciasSeleccionadas.length > 0 && (
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="p-3 flex items-center justify-between">
            <span className="text-blue-700">
              {sentenciasSeleccionadas.length} sentencia(s) seleccionada(s)
            </span>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={() => setSentenciasSeleccionadas([])}>
                Deseleccionar
              </Button>
              <Button size="sm" onClick={() => setShowIAModal(true)}>
                <Bot className="w-4 h-4 mr-1" />
                Analizar con IA
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Listado de sentencias */}
      <div className="grid gap-4">
        {sentencias.length === 0 ? (
          <Card className="p-12 text-center">
            <FileText className="w-16 h-16 mx-auto text-gray-300 mb-4" />
            <h3 className="text-lg font-medium text-gray-600 mb-2">No hay sentencias</h3>
            <p className="text-gray-500 mb-4">Sube tu primera sentencia para crear el fondo jurisprudencial</p>
            <Button onClick={() => setShowUploadModal(true)}>
              <Upload className="w-4 h-4 mr-2" />
              Subir Sentencia
            </Button>
          </Card>
        ) : (
          sentencias.map(s => (
            <Card 
              key={s.id} 
              className={`hover:shadow-md transition-all cursor-pointer ${
                sentenciasSeleccionadas.includes(s.id) ? 'ring-2 ring-blue-500 bg-blue-50' : ''
              } ${s.destacada ? 'border-l-4 border-l-yellow-400' : ''}`}
            >
              <CardContent className="p-4">
                <div className="flex items-start gap-4">
                  {/* Checkbox para selección */}
                  <div 
                    className="mt-1 cursor-pointer"
                    onClick={(e) => { e.stopPropagation(); toggleSeleccion(s.id); }}
                  >
                    <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                      sentenciasSeleccionadas.includes(s.id) 
                        ? 'bg-blue-600 border-blue-600 text-white' 
                        : 'border-gray-300'
                    }`}>
                      {sentenciasSeleccionadas.includes(s.id) && '✓'}
                    </div>
                  </div>
                  
                  {/* Icono PDF */}
                  <div className="p-2 bg-red-100 rounded-lg">
                    <FileText className="w-6 h-6 text-red-600" />
                  </div>
                  
                  {/* Contenido */}
                  <div className="flex-1" onClick={() => verDetalle(s.id)}>
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="font-semibold text-gray-900">
                          Sentencia {s.numero_sentencia}
                          {s.destacada && <Star className="w-4 h-4 inline ml-2 text-yellow-500 fill-yellow-500" />}
                        </h3>
                        <p className="text-sm text-gray-600">{s.juzgado}</p>
                      </div>
                      <Badge variant="outline" style={{
                        backgroundColor: categorias.find(c => c.id === s.categoria_id)?.color + '20',
                        borderColor: categorias.find(c => c.id === s.categoria_id)?.color
                      }}>
                        {s.categoria_nombre || 'Sin categoría'}
                      </Badge>
                    </div>
                    
                    <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3.5 h-3.5" />
                        {formatFecha(s.fecha)}
                      </span>
                      <span className="flex items-center gap-1">
                        <Tag className="w-3.5 h-3.5" />
                        {s.materia}
                      </span>
                      {s.subtema && (
                        <span className="text-blue-600">{s.subtema}</span>
                      )}
                      <span className="text-gray-400">{formatSize(s.archivo_size)}</span>
                    </div>
                    
                    {s.resumen_ia && (
                      <p className="mt-2 text-sm text-gray-600 line-clamp-2">
                        {s.resumen_ia.substring(0, 200)}...
                      </p>
                    )}
                  </div>
                  
                  {/* Acciones */}
                  <div className="flex items-center gap-1">
                    <Button 
                      size="sm" 
                      variant="ghost"
                      title="Destacar"
                      onClick={(e) => { e.stopPropagation(); toggleDestacada(s); }}
                    >
                      {s.destacada ? <StarOff className="w-4 h-4" /> : <Star className="w-4 h-4" />}
                    </Button>
                    <Button 
                      size="sm" 
                      variant="ghost"
                      title="Visualizar PDF"
                      onClick={(e) => { 
                        e.stopPropagation(); 
                        window.open(`${process.env.REACT_APP_BACKEND_URL}/api/sentencias/${s.id}/visualizar`, '_blank');
                      }}
                    >
                      <Eye className="w-4 h-4" />
                    </Button>
                    <Button 
                      size="sm" 
                      variant="ghost"
                      title="Descargar PDF"
                      onClick={(e) => { 
                        e.stopPropagation(); 
                        window.open(`${process.env.REACT_APP_BACKEND_URL}/api/sentencias/${s.id}/descargar`, '_blank');
                      }}
                    >
                      <Download className="w-4 h-4" />
                    </Button>
                    <Button 
                      size="sm" 
                      variant="ghost"
                      title="Ver/Editar detalle"
                      onClick={(e) => { e.stopPropagation(); verDetalle(s.id); }}
                    >
                      <PenTool className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Modal de Subida */}
      <Dialog open={showUploadModal} onOpenChange={setShowUploadModal}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="w-5 h-5 text-blue-600" />
              Subir Nueva Sentencia
            </DialogTitle>
            <DialogDescription>
              Sube un archivo PDF de la sentencia y completa los datos
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleUpload} className="space-y-4">
            {/* Archivo */}
            <div 
              className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
                uploadData.archivo ? 'border-green-400 bg-green-50' : 'border-gray-300 hover:border-blue-400'
              }`}
              onDragEnter={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              onDragOver={(e) => {
                e.preventDefault();
                e.stopPropagation();
                e.currentTarget.classList.add('border-blue-500', 'bg-blue-50');
              }}
              onDragLeave={(e) => {
                e.preventDefault();
                e.stopPropagation();
                e.currentTarget.classList.remove('border-blue-500', 'bg-blue-50');
              }}
              onDrop={(e) => {
                e.preventDefault();
                e.stopPropagation();
                e.currentTarget.classList.remove('border-blue-500', 'bg-blue-50');
                const files = e.dataTransfer.files;
                if (files && files.length > 0) {
                  const file = files[0];
                  if (file.type === 'application/pdf' || file.name.endsWith('.pdf')) {
                    setUploadData({...uploadData, archivo: file});
                    toast({ title: "Archivo cargado", description: file.name });
                  } else {
                    toast({ title: "Error", description: "Solo se permiten archivos PDF", variant: "destructive" });
                  }
                }
              }}
            >
              {uploadData.archivo ? (
                <div className="flex items-center justify-center gap-3">
                  <FileText className="w-8 h-8 text-red-600" />
                  <div className="text-left">
                    <p className="font-medium">{uploadData.archivo.name}</p>
                    <p className="text-sm text-gray-500">{formatSize(uploadData.archivo.size)}</p>
                  </div>
                  <Button 
                    type="button" 
                    variant="ghost" 
                    size="sm"
                    onClick={() => setUploadData({...uploadData, archivo: null})}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ) : (
                <label className="cursor-pointer">
                  <FileUp className="w-12 h-12 mx-auto text-gray-400 mb-2" />
                  <p className="text-gray-600">Haz clic o arrastra un archivo PDF</p>
                  <input
                    type="file"
                    accept=".pdf"
                    className="hidden"
                    onChange={(e) => setUploadData({...uploadData, archivo: e.target.files[0]})}
                  />
                </label>
              )}
            </div>
            
            {/* Datos obligatorios */}
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label>Juzgado *</Label>
                <Input
                  required
                  placeholder="Ej: Juzgado de Primera Instancia nº 5 de Barcelona"
                  value={uploadData.juzgado}
                  onChange={(e) => setUploadData({...uploadData, juzgado: e.target.value})}
                />
              </div>
              <div>
                <Label>Número de Sentencia *</Label>
                <Input
                  required
                  placeholder="Ej: 123/2024"
                  value={uploadData.numero_sentencia}
                  onChange={(e) => setUploadData({...uploadData, numero_sentencia: e.target.value})}
                />
              </div>
              <div>
                <Label>Fecha *</Label>
                <Input
                  required
                  type="date"
                  value={uploadData.fecha}
                  onChange={(e) => setUploadData({...uploadData, fecha: e.target.value})}
                />
              </div>
              <div>
                <Label>Materia *</Label>
                <Input
                  required
                  placeholder="Ej: Civil, Penal, Laboral..."
                  value={uploadData.materia}
                  onChange={(e) => setUploadData({...uploadData, materia: e.target.value})}
                />
              </div>
              <div>
                <Label>Categoría</Label>
                <Select 
                  value={uploadData.categoria_id} 
                  onValueChange={(v) => setUploadData({...uploadData, categoria_id: v})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar..." />
                  </SelectTrigger>
                  <SelectContent>
                    {categorias.map(cat => (
                      <SelectItem key={cat.id} value={cat.id.toString()}>
                        {cat.nombre}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2">
                <Label>Subtema (libre)</Label>
                <Input
                  placeholder="Ej: Cláusulas suelo, Despido improcedente..."
                  value={uploadData.subtema}
                  onChange={(e) => setUploadData({...uploadData, subtema: e.target.value})}
                />
              </div>
              <div className="col-span-2">
                <Label>Notas</Label>
                <Textarea
                  placeholder="Notas adicionales sobre esta sentencia..."
                  value={uploadData.notas}
                  onChange={(e) => setUploadData({...uploadData, notas: e.target.value})}
                  rows={2}
                />
              </div>
            </div>
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowUploadModal(false)}>
                Cancelar
              </Button>
              <Button type="submit" disabled={uploading}>
                {uploading ? <><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Subiendo...</> : 'Subir Sentencia'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Modal de Detalle */}
      <Dialog open={showDetalleModal} onOpenChange={setShowDetalleModal}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          {sentenciaSeleccionada && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Scale className="w-5 h-5 text-blue-600" />
                  Sentencia {sentenciaSeleccionada.numero_sentencia}
                  {sentenciaSeleccionada.destacada && (
                    <Star className="w-5 h-5 text-yellow-500 fill-yellow-500" />
                  )}
                </DialogTitle>
                <DialogDescription>{sentenciaSeleccionada.juzgado}</DialogDescription>
              </DialogHeader>
              
              <div className="space-y-4">
                {/* Datos principales */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-gray-500">Fecha</Label>
                    <p className="font-medium">{formatFecha(sentenciaSeleccionada.fecha)}</p>
                  </div>
                  <div>
                    <Label className="text-gray-500">Materia</Label>
                    <p className="font-medium">{sentenciaSeleccionada.materia}</p>
                  </div>
                  <div>
                    <Label className="text-gray-500">Categoría</Label>
                    <p className="font-medium">{sentenciaSeleccionada.categoria_nombre || 'Sin categoría'}</p>
                  </div>
                  <div>
                    <Label className="text-gray-500">Subtema</Label>
                    <p className="font-medium">{sentenciaSeleccionada.subtema || '-'}</p>
                  </div>
                </div>
                
                {/* Resumen IA */}
                <div className="bg-purple-50 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-medium flex items-center gap-2">
                      <Brain className="w-4 h-4 text-purple-600" />
                      Resumen IA
                    </h4>
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => generarResumen(sentenciaSeleccionada.id)}
                      disabled={iaLoading}
                    >
                      {iaLoading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4 mr-1" />}
                      {sentenciaSeleccionada.resumen_ia ? 'Regenerar' : 'Generar'}
                    </Button>
                  </div>
                  {sentenciaSeleccionada.resumen_ia ? (
                    <div className="prose prose-sm max-w-none text-gray-700 whitespace-pre-wrap">
                      {sentenciaSeleccionada.resumen_ia}
                    </div>
                  ) : (
                    <p className="text-gray-500 text-sm">
                      Haz clic en "Generar" para crear un resumen automático
                    </p>
                  )}
                </div>
                
                {/* Notas */}
                {sentenciaSeleccionada.notas && (
                  <div>
                    <Label className="text-gray-500">Notas</Label>
                    <p className="text-gray-700">{sentenciaSeleccionada.notas}</p>
                  </div>
                )}
                
                {/* Archivo */}
                <div className="flex items-center justify-between bg-gray-50 rounded-lg p-3">
                  <div className="flex items-center gap-3">
                    <FileText className="w-8 h-8 text-red-600" />
                    <div>
                      <p className="font-medium">{sentenciaSeleccionada.archivo_nombre}</p>
                      <p className="text-sm text-gray-500">{formatSize(sentenciaSeleccionada.archivo_size)}</p>
                    </div>
                  </div>
                  <Button
                    onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/sentencias/${sentenciaSeleccionada.id}/descargar`, '_blank')}
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Descargar
                  </Button>
                </div>
              </div>
              
              <DialogFooter className="flex gap-2">
                <Button 
                  variant="destructive" 
                  onClick={() => eliminarSentencia(sentenciaSeleccionada.id)}
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Eliminar
                </Button>
                <Button 
                  variant="default"
                  onClick={() => abrirEdicion(sentenciaSeleccionada)}
                >
                  <PenTool className="w-4 h-4 mr-2" />
                  Editar
                </Button>
                <Button variant="outline" onClick={() => setShowDetalleModal(false)}>
                  Cerrar
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Modal de IA */}
      <Dialog open={showIAModal} onOpenChange={setShowIAModal}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bot className="w-5 h-5 text-purple-600" />
              Consultar Fondo Jurisprudencial con IA
            </DialogTitle>
            <DialogDescription>
              Pregunta sobre las sentencias almacenadas o analiza múltiples sentencias
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Tipo de análisis */}
            <div>
              <Label>Tipo de análisis</Label>
              <Select value={iaTipoAnalisis} onValueChange={setIaTipoAnalisis}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="consultar">🔍 Consultar fondo jurisprudencial</SelectItem>
                  <SelectItem value="comparar">⚖️ Comparar sentencias seleccionadas</SelectItem>
                  <SelectItem value="argumentos">📝 Generar argumentos jurídicos</SelectItem>
                  <SelectItem value="resumen">📋 Resumen conjunto</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Sentencias seleccionadas */}
            {sentenciasSeleccionadas.length > 0 && iaTipoAnalisis !== 'consultar' && (
              <div className="bg-blue-50 rounded-lg p-3">
                <p className="text-sm text-blue-700">
                  <strong>{sentenciasSeleccionadas.length}</strong> sentencia(s) seleccionada(s) para análisis
                </p>
              </div>
            )}
            
            {/* Consulta */}
            <div>
              <Label>Tu consulta</Label>
              <Textarea
                placeholder={
                  iaTipoAnalisis === 'consultar' 
                    ? "Ej: ¿Qué criterios aplican los tribunales en casos de cláusulas suelo?"
                    : iaTipoAnalisis === 'argumentos'
                    ? "Describe el caso para el que necesitas argumentos..."
                    : "Describe qué quieres analizar..."
                }
                value={iaQuery}
                onChange={(e) => setIaQuery(e.target.value)}
                rows={3}
              />
            </div>
            
            <Button 
              onClick={consultarIA} 
              disabled={iaLoading || !iaQuery.trim()}
              className="w-full"
            >
              {iaLoading ? (
                <><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Analizando...</>
              ) : (
                <><Sparkles className="w-4 h-4 mr-2" />Consultar IA</>
              )}
            </Button>
            
            {/* Respuesta */}
            {iaResponse && (
              <div className="bg-gray-50 rounded-lg p-4 mt-4">
                <h4 className="font-medium mb-2 flex items-center gap-2">
                  <MessageSquare className="w-4 h-4 text-green-600" />
                  Respuesta
                  {iaResponse.sentencias_consultadas && (
                    <Badge variant="outline" className="ml-2">
                      {iaResponse.sentencias_consultadas} sentencias consultadas
                    </Badge>
                  )}
                </h4>
                <div className="prose prose-sm max-w-none whitespace-pre-wrap text-gray-700">
                  {iaResponse.respuesta || iaResponse.analisis}
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal de Edición */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>Editar Sentencia</DialogTitle>
            <DialogDescription>
              Modifica los datos de la sentencia
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={guardarEdicion}>
            <div className="grid gap-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Juzgado/Tribunal</Label>
                  <Input
                    value={editData.juzgado}
                    onChange={(e) => setEditData({...editData, juzgado: e.target.value})}
                    placeholder="Ej: Audiencia Provincial de Barcelona"
                  />
                </div>
                <div>
                  <Label>Número de Sentencia</Label>
                  <Input
                    value={editData.numero_sentencia}
                    onChange={(e) => setEditData({...editData, numero_sentencia: e.target.value})}
                    placeholder="Ej: 123/2024"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Fecha</Label>
                  <Input
                    type="date"
                    value={editData.fecha}
                    onChange={(e) => setEditData({...editData, fecha: e.target.value})}
                  />
                </div>
                <div>
                  <Label>Materia</Label>
                  <Input
                    value={editData.materia}
                    onChange={(e) => setEditData({...editData, materia: e.target.value})}
                    placeholder="Ej: Derecho Civil, Penal, Laboral..."
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Categoría</Label>
                  <Select 
                    value={editData.categoria_id?.toString() || ''} 
                    onValueChange={(v) => setEditData({...editData, categoria_id: v})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Seleccionar categoría" />
                    </SelectTrigger>
                    <SelectContent>
                      {categorias.map(cat => (
                        <SelectItem key={cat.id} value={cat.id.toString()}>
                          {cat.nombre}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Subtema</Label>
                  <Input
                    value={editData.subtema}
                    onChange={(e) => setEditData({...editData, subtema: e.target.value})}
                    placeholder="Ej: Cláusulas suelo"
                  />
                </div>
              </div>
              
              <div>
                <Label>Notas</Label>
                <Textarea
                  value={editData.notas}
                  onChange={(e) => setEditData({...editData, notas: e.target.value})}
                  placeholder="Observaciones adicionales..."
                  rows={3}
                />
              </div>
            </div>
            
            <DialogFooter className="flex gap-2">
              <Button 
                type="button"
                variant="destructive" 
                onClick={() => sentenciaSeleccionada && eliminarSentencia(sentenciaSeleccionada.id)}
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Eliminar
              </Button>
              <Button type="button" variant="outline" onClick={() => setShowEditModal(false)}>
                Cancelar
              </Button>
              <Button type="submit" disabled={saving}>
                {saving && <RefreshCw className="w-4 h-4 mr-2 animate-spin" />}
                Guardar Cambios
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Sentencias;
